import React, { useEffect, useState } from "react";

import { useExploration, STATES } from "../../../services/useExploration";
import StageRail from "../../components/StageRail";
import SourceLane from "../../components/SourceLane";
import { STEPS } from "../../../services/creativeSessionStep";
import "./exploration.css";

/**
 * Workstation · Ads & Creative — **Research Exploration**, in v2.
 *
 * RESEARCH-EXPLORATION-REDESIGN, 2026-09-10. One screen, approved for
 * production evaluation before the rest of the Creative Session follows.
 *
 * WHY THIS IS A NEW FILE RATHER THAN A RESTYLE. v1's `ExplorationPhase` is
 * imported by `CreativeEngine.jsx` too, and v1 does not render inside
 * `.cipher-ui` — restyling in place would have put class names on a surface that
 * cannot see them. The state machine is shared through
 * `services/useExploration` so the two cannot decide differently; the old
 * component keeps its own presentation until wave 5 retires v1.
 *
 * THE STANDING RULE THIS SCREEN IS BUILT UNDER, Founder 2026-09-10: "no seeded
 * or mock data anywhere, and nothing shown on screen may be a placeholder
 * standing in for real information. Every number, label, and state must come
 * from a real document field or a real computed value."
 *
 * So, concretely, on this screen:
 *   - the source lanes are the service's `KIND_CATALOGUE`, sent with the
 *     document. The screen cannot offer a source the fetcher does not route —
 *     which is how four dead source names survived here for months;
 *   - readiness is a list of REAL blockers, each naming the field it read. There
 *     is no score;
 *   - the objective is a stored field. When the proposer did not write one the
 *     screen says so and offers to write it, rather than assembling a sentence
 *     that looks like one;
 *   - the round budget and "what a run costs" come from `doc.budget`, and are
 *     omitted when the document has none;
 *   - `BRIEF_FAILED` is rendered. v1 draws a bare header there while the service
 *     documents the retry as available.
 */

//: Exploration, then the seven the wizard actually has. Founder ruling: the
//: honest eight, not a forced seven.
const STAGES = [
  { id: "exploration", label: "Exploration", hint: "Research the landscape" },
  ...STEPS.map((s) => ({ id: s.id, label: s.label, reachable: false })),
];

/** The Creative Agent's suggested openers. Each is a real instruction sent to
 *  the real steering endpoint — none is a decorative chip. */
const SUGGESTIONS = [
  { id: "objections", text: "What are the strongest objections in what you found?" },
  { id: "motivations", text: "What actually motivates these buyers, in their own words?" },
  { id: "themes", text: "Summarise the themes across every source." },
  { id: "surprises", text: "What surprised you in these findings?" },
];

export default function ExplorationScreen({ sessionId, offer, onProceed, onSkip,
                                            onClose }) {
  const X = useExploration({ sessionId, onProceed });

  const [editingObjective, setEditingObjective] = useState(false);
  const [objectiveDraft, setObjectiveDraft] = useState("");
  const [message, setMessage] = useState("");
  const [feedback, setFeedback] = useState("");

  const doc = X.doc;
  const objective = doc?.objective || null;

  useEffect(() => { setObjectiveDraft(objective || ""); }, [objective]);

  // The suggested openers steer research that has happened. Before a fetch there
  // are no findings to ask about, so they are disabled WITH THE REASON rather
  // than shown as controls that quietly do nothing.
  const canSteer = [STATES.FINDINGS, STATES.CONFIRMED].includes(X.state);
  const steerWhy = canSteer ? null
    : "Available once a research round has run — there are no findings to ask about yet.";

  const busy = !!X.busy;

  return (
    <div className="page xp" data-testid="xp-screen">
      {/* ── 1 · SESSION HEADER ─────────────────────────────────────────────── */}
      <header className="xp-head">
        <div className="xp-head-top">
          <button type="button" className="btn-ghost btn-sm" onClick={onClose}
                  data-testid="xp-back">← All sessions</button>
          <div className="xp-title">
            <h1 className="h1">{offer?.name || "Creative session"}</h1>
            <p className="sub">
              <strong>Exploration</strong> · research before any creative is written.
              What comes out of this becomes the evidence the Psychology Map reads.
            </p>
          </div>
        </div>
        <StageRail stages={STAGES} activeIndex={0} testid="xp-rail" />
      </header>

      {X.loadState === "loading" ? (
        <div className="state" data-testid="xp-loading">
          <div className="skeleton-stack" aria-hidden="true">
            <div className="skeleton" /><div className="skeleton" /><div className="skeleton" />
          </div>
          <p className="dim">Scoping this offer — the proposer is writing the
             objective and the first research targets.</p>
        </div>
      ) : null}

      {X.loadState === "failed" ? (
        <div className="state state-error" role="alert" data-testid="xp-load-failed">
          <p>Exploration could not be started: {X.loadError}</p>
          <p className="dim">This is a failure, not a session with nothing to
             research. Nothing has been lost — you can also skip straight to the
             Psychology Map.</p>
          <button type="button" className="btn-ghost btn-sm" onClick={onSkip}
                  data-testid="xp-skip-on-error">Skip to Psychology Map</button>
        </div>
      ) : null}

      {X.actionError ? (
        <p className="state-error xp-note" role="alert" data-testid="xp-action-error">
          {X.actionError}
        </p>
      ) : null}

      {X.loadState === "ok" && doc ? (
        <div className="with-rail xp-body">
          {/* ── MAIN WORKSPACE ─────────────────────────────────────────────── */}
          <div className="xp-main">

            {/* ── 2 · RESEARCH MISSION ───────────────────────────────────── */}
            <section className="card xp-mission" data-testid="xp-mission">
              <div className="card-head">
                <span className="label accent-c">Research mission</span>
                {objective && !editingObjective ? (
                  <button type="button" className="btn-ghost btn-sm"
                          onClick={() => setEditingObjective(true)}
                          data-testid="xp-objective-edit">Edit objective</button>
                ) : null}
              </div>

              {editingObjective ? (
                <div className="xp-objective-edit">
                  <label className="label" htmlFor="xp-objective">
                    The objective, in your words
                  </label>
                  <textarea id="xp-objective" rows={3} value={objectiveDraft}
                            onChange={(e) => setObjectiveDraft(e.target.value)}
                            data-testid="xp-objective-input" />
                  <div className="xp-objective-foot">
                    <button type="button" className="btn-ghost btn-sm"
                            onClick={() => { setEditingObjective(false);
                                             setObjectiveDraft(objective || ""); }}
                            data-testid="xp-objective-cancel">Discard</button>
                    <button type="button" className="btn-primary btn-sm"
                            disabled={!objectiveDraft.trim() || busy}
                            onClick={async () => {
                              const ok = await X.saveObjective(objectiveDraft);
                              if (ok) setEditingObjective(false);
                            }}
                            data-testid="xp-objective-save">
                      {busy ? "Saving…" : "Save objective"}
                    </button>
                  </div>
                </div>
              ) : objective ? (
                <>
                  <p className="xp-objective" data-testid="xp-objective-text">{objective}</p>
                  <p className="dim xp-objective-src" data-testid="xp-objective-source">
                    {doc.objective_source === "OPERATOR"
                      ? "Written by you."
                      : "Written by the proposer from this offer's metadata."}
                  </p>
                </>
              ) : (
                /* UNSET IS A REAL STATE. The proposer did not write one — this
                   does not assemble a sentence to fill the space. */
                <div data-testid="xp-objective-unset">
                  <p className="xp-objective is-unset">No objective has been written.</p>
                  <p className="dim">
                    The proposer writes one from the offer's metadata when it
                    scopes the research; it did not on this session. You can write
                    it yourself — everything below is still usable without one.
                  </p>
                  <button type="button" className="btn-primary btn-sm"
                          onClick={() => setEditingObjective(true)}
                          data-testid="xp-objective-write">Write the objective</button>
                </div>
              )}
            </section>

            {/* ── 3 · RESEARCH TARGETS BY SOURCE ─────────────────────────── */}
            <section className="xp-lanes" data-testid="xp-lanes">
              <div className="xp-lanes-head">
                <div>
                  <h2 className="h2">Research targets by source</h2>
                  <p className="dim">
                    Each query is sent to the one source that can answer it.
                    {X.readiness.sourcesRegistered
                      ? ` This session can reach ${X.readiness.sourcesRegistered} `
                        + `source${X.readiness.sourcesRegistered === 1 ? "" : "s"}.`
                      : ""}
                  </p>
                </div>
                {/* SUGGESTED / EDITED, from the stored origin field. Shown only
                    once the service has recorded origins — the documents written
                    before that say so instead of being counted as suggestions. */}
                {X.origins.recorded ? (
                  <p className="xp-origins" data-testid="xp-origins">
                    {X.origins.suggested ? <span className="chip">{X.origins.suggested} suggested</span> : null}
                    {X.origins.edited ? <span className="chip is-edited">{X.origins.edited} edited</span> : null}
                    {X.origins.added ? <span className="chip is-edited">{X.origins.added} added by you</span> : null}
                    {X.origins.unknown ? <span className="chip is-unknown">{X.origins.unknown} origin not recorded</span> : null}
                  </p>
                ) : null}
              </div>

              {X.lanes.length === 0 ? (
                <div className="state" data-testid="xp-lanes-empty">
                  <p>The list of sources could not be read from the server, so
                     this screen cannot say what it would query.</p>
                  <p className="dim">It is not showing a guess. Reload the
                     session to try again.</p>
                </div>
              ) : X.lanes.map((lane) => (
                <SourceLane key={lane.kind} lane={lane} busy={busy}
                            editable={X.reviewing}
                            /* A retry only exists after a round has run, and
                               only for a source that is actually included. The
                               service refuses both cases anyway, in words — this
                               keeps the control off screen rather than offering
                               an action that will be declined. */
                            onRetry={!X.reviewing && lane.selected
                                       ? X.retrySource : null}
                            onToggle={X.reviewing ? X.toggleSource : null}
                            onChangeQuery={(i, v) => X.setTarget(i, { label: v })}
                            onRemoveQuery={X.removeTarget}
                            onAddQuery={X.addTarget} />
              ))}
            </section>

            {/* ── FINDINGS, once a round has run ─────────────────────────── */}
            {X.lastRound ? (
              <section className="card" data-testid="xp-findings">
                <div className="card-head">
                  <span className="label accent-c">What came back</span>
                  <span className="dim">
                    round {X.readiness.roundsUsed ?? "?"} of {X.readiness.roundsMax ?? "?"}
                  </span>
                </div>
                <ul className="xp-results">
                  {(X.lastRound.results || []).map((r, i) => {
                    const items = r.items || [];
                    return (
                      <li key={i} data-testid={`xp-result-${i}`}>
                        <div className="xp-result-head">
                          <span className="mono">{r.source}</span>
                          <span className="dim">“{X.lastRound.queries?.[i]?.topic}”</span>
                          {/* Failed, empty and populated are three different
                              outcomes and read as three different things.
                              2026-09-11: the WORDS were already right here, but
                              a failure and an empty answer shared the amber
                              pill — so at a glance they still looked alike, and
                              the source's own reason (`r.error`, on every result
                              row) was dropped. A fault is `crit` now, matching
                              the lanes, and it says why. */}
                          <span className={`pill ${r.ok === false ? "crit" : items.length ? "ok" : "warn"}`}
                                title={r.ok === false && r.error ? String(r.error) : undefined}>
                            {r.ok === false ? "the source failed"
                              : items.length ? `${items.length} found` : "nothing found"}
                          </span>
                          {r.ok === false && r.error ? (
                            <span className="dim xp-result-why">— {String(r.error)}</span>
                          ) : null}
                        </div>
                        {items.slice(0, 2).map((it, j) => (
                          <p key={j} className="xp-result-item">
                            {(it.title || "").slice(0, 120)}
                          </p>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {/* ── BRIEF IN REVIEW ────────────────────────────────────────── */}
            {X.state === STATES.REVIEW && doc.brief ? (
              <section className="card" data-testid="xp-brief">
                <div className="card-head">
                  <span className="label accent-c">The research brief</span>
                  <span className="dim mono">v{doc.brief.version}</span>
                </div>
                <pre className="xp-brief-text" data-testid="xp-brief-text">{doc.brief.text}</pre>
                <div className="xp-brief-foot">
                  <button type="button" className="btn-primary btn-sm"
                          onClick={X.approveBrief} disabled={busy}
                          data-testid="xp-brief-approve">
                    {busy ? "Locking…" : "Approve — this becomes the evidence brief"}
                  </button>
                  <div className="xp-revise">
                    <label className="label" htmlFor="xp-feedback">
                      Or say what to change
                    </label>
                    <textarea id="xp-feedback" rows={2} value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                              data-testid="xp-feedback-input" />
                    <button type="button" className="btn-ghost btn-sm"
                            disabled={busy || feedback.trim().length < 3}
                            onClick={async () => {
                              const ok = await X.reviseBrief(feedback);
                              if (ok) setFeedback("");
                            }}
                            data-testid="xp-brief-revise">Request a revision</button>
                  </div>
                </div>
              </section>
            ) : null}

            {/* ── BRIEF FAILED — v1 renders nothing here ─────────────────── */}
            {X.state === STATES.FAILED ? (
              <section className="state state-error" role="alert" data-testid="xp-brief-failed">
                <p>The brief could not be written from what was found.</p>
                <p className="dim">
                  The research itself is still here — this is the synthesis
                  failing, not the findings being lost. Try again, gather another
                  round first, or skip to the Psychology Map without a brief.
                </p>
                <button type="button" className="btn-primary btn-sm"
                        onClick={X.synthesise} disabled={busy}
                        data-testid="xp-brief-retry">
                  {busy ? "Writing…" : "Try writing the brief again"}
                </button>
              </section>
            ) : null}

            {X.state === STATES.LOCKED ? (
              <section className="state" data-testid="xp-locked">
                <p>Research locked. Opening the Psychology Map…</p>
              </section>
            ) : null}

            {/* ── 6 · PRIMARY ACTIONS ────────────────────────────────────── */}
            <footer className="xp-actions" data-testid="xp-actions">
              <div className="xp-actions-say">
                {/* WHAT WILL HAPPEN, and the numbers come from `doc.budget` —
                    omitted entirely when the document has none. */}
                <p>
                  {X.state === STATES.PROPOSED
                    ? "Confirming sends each query to its source and returns real results."
                    : "Running research sends each query to its source and returns real results."}
                  {" "}The findings become the evidence brief the Psychology Map reads.
                </p>
                {X.readiness.roundsMax !== null ? (
                  <p className="dim" data-testid="xp-budget">
                    {X.readiness.roundsUsed !== null
                      ? `Round ${X.readiness.roundsUsed + 1} of ${X.readiness.roundsMax}. `
                      : ""}
                    {X.readiness.itemsPerSource !== null
                      ? `Up to ${X.readiness.itemsPerSource} results per source. `
                      : ""}
                    A round takes up to a minute.
                  </p>
                ) : null}
              </div>

              <div className="xp-actions-btns">
                <button type="button" className="btn-ghost btn-sm" onClick={onSkip}
                        disabled={busy} data-testid="xp-skip">
                  Skip exploration
                </button>

                {/* ONE primary, and which one depends on the state. */}
                {X.state === STATES.PROPOSED ? (
                  <button type="button" className="btn-primary"
                          onClick={X.confirmTargets}
                          disabled={busy || !X.readiness.ready}
                          data-testid="xp-confirm">
                    {X.busy || "Confirm the research plan"}
                  </button>
                ) : [STATES.CONFIRMED, STATES.FINDINGS].includes(X.state) ? (
                  <button type="button" className="btn-primary"
                          onClick={X.runFetch} disabled={busy}
                          data-testid="xp-run">
                    {X.busy || "Run research"}
                  </button>
                ) : null}

                {/* Synthesis is secondary — it ends the gathering. */}
                {[STATES.CONFIRMED, STATES.FINDINGS].includes(X.state) ? (
                  <button type="button" className="btn-ghost btn-sm"
                          onClick={X.synthesise} disabled={busy}
                          data-testid="xp-synthesise">
                    Write the brief from what we have
                  </button>
                ) : null}
              </div>
            </footer>
          </div>

          {/* ── RIGHT RAIL ─────────────────────────────────────────────────── */}
          <aside className="rail xp-rail-col">

            {/* ── 4 · RESEARCH READINESS ─────────────────────────────────── */}
            <section className="card" data-testid="xp-readiness">
              <div className="card-head">
                <span className="label accent-c">Research readiness</span>
                <span className={`pill ${X.readiness.ready ? "ok" : "warn"}`}
                      data-testid="xp-readiness-state">
                  {X.readiness.ready ? "ready to run" : "not ready"}
                </span>
              </div>

              {/* THE INSTRUMENT. Same two numbers as before — the Founder asked
                  for them to read as a gauge rather than two statistic cards, so
                  the arc is drawn FROM those numbers. Its sweep is
                  sourcesSelected / sourcesRegistered; there is no invented
                  score behind it. */}
              <div className="xp-gauge">
                <div className="xp-dial" aria-hidden="true">
                  <svg viewBox="0 0 96 96">
                    <circle className="xp-dial-track" cx="48" cy="48" r="38" />
                    {/* ONE MEANING PER CHANNEL. The sweep encodes coverage —
                        sources selected over sources reachable — and nothing
                        else. It was also being tinted amber when readiness
                        failed, which made a FULL ring read as an alarm and
                        double-encoded a fact the pill and the blockers below
                        already state twice. */}
                    <circle
                      className="xp-dial-arc"
                      cx="48" cy="48" r="38"
                      style={{
                        strokeDasharray: 2 * Math.PI * 38,
                        strokeDashoffset: 2 * Math.PI * 38 * (1 - (
                          X.readiness.sourcesRegistered
                            ? X.readiness.sourcesSelected / X.readiness.sourcesRegistered
                            : 0)),
                      }} />
                  </svg>
                  <div className="xp-dial-mid">
                    <span className="xp-dial-v" data-testid="xp-kpi-sources">
                      {X.readiness.sourcesSelected}
                    </span>
                    <span className="xp-dial-k">
                      of {X.readiness.sourcesRegistered} sources
                    </span>
                  </div>
                </div>

                <dl className="xp-gauge-facts">
                  <div>
                    <dt>Queries written</dt>
                    <dd data-testid="xp-kpi-queries">{X.readiness.labelledQueries}</dd>
                    <span>of {X.readiness.totalQueries} on the plan</span>
                  </div>
                  <div>
                    <dt>Channels engaged</dt>
                    <dd>{X.lanes.filter((l) => l.selected).length}</dd>
                    <span>of {X.lanes.length} this session can reach</span>
                  </div>
                </dl>
              </div>

              {/* Per-channel coverage, from the same lane data the workspace
                  draws — so the rail and the lanes cannot disagree. */}
              <ul className="xp-cover" data-testid="xp-coverage">
                {X.lanes.map((l) => {
                  const n = l.queries.filter((q) => (q.label || "").trim()).length;
                  return (
                    <li key={l.kind} className={l.selected ? "" : "is-off"}>
                      <span className="xp-cover-name">{l.label}</span>
                      <span className={`meter ${!l.selected ? "" : n ? "ok" : "warn"}`}>
                        <b style={{ width: l.selected && n ? "100%" : "0%" }} />
                      </span>
                      <span className="xp-cover-n">
                        {l.selected ? `${n}` : "off"}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* TWO LISTS, BECAUSE THEY MEAN TWO THINGS. A blocker stops
                  Confirm; a warning is a cost the run carries. They were one
                  list, which is how an optional empty source came to look like
                  a fault and disable the button. Rule 10: a warning must not be
                  indistinguishable from a failure, in either direction. */}
              {X.readiness.blockers.length > 0 ? (
                <ul className="xp-blockers" data-testid="xp-blockers">
                  {X.readiness.blockers.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              ) : null}

              {X.readiness.warnings.length > 0 ? (
                <ul className="xp-warnings" data-testid="xp-warnings">
                  {X.readiness.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              ) : null}

              {/* AND A COUNT, so an empty source is visible without scrolling
                  five lanes to find it. Derived from the lanes, which derive
                  from the round's own result rows — no new state. */}
              {(() => {
                const ran = X.lanes.filter((l) => l.selected && l.lastRun?.ran);
                const bad = ran.filter((l) => ["empty", "failed", "partial"]
                  .includes(l.lastRun.outcome));
                if (!bad.length) return null;
                return (
                  <p className="xp-round-tally" role="status"
                     data-testid="xp-round-tally">
                    Last round: <b>{bad.length} of {ran.length}</b> source
                    {ran.length === 1 ? "" : "s"} came back with nothing usable
                    — {bad.map((l) => l.label).join(", ")}. Each one says why,
                    and can be retried with a different query.
                  </p>
                );
              })()}

              {/* THE BASIS THE GATE RAN ON. `subject_usable: false` means
                  nothing was judged — which is not the same as everything
                  having passed — and it says which. */}
              {X.relevance && X.lastRound ? (
                <p className="xp-gate-basis" data-testid="xp-gate-basis">
                  {X.relevance.subject_usable
                    ? <>Ad results were checked against this offer&apos;s own
                        subject: <b>{X.relevance.subject}</b>.</>
                    : <>Ad results were <b>not</b> checked for subject —{" "}
                        {X.relevance.reason || "the offer's subject could not be established"}.</>}
                </p>
              ) : null}

              {X.readiness.blockers.length === 0
               && X.readiness.warnings.length === 0 ? (
                <p className="dim" data-testid="xp-readiness-clear">
                  Every selected source has a query, and every query has
                  somewhere to go.
                </p>
              ) : null}

              {/* Say WHY the button is live while something is still flagged,
                  so "ready with warnings" is not mistaken for "nothing wrong". */}
              {X.readiness.blockers.length === 0
               && X.readiness.warnings.length > 0 ? (
                <p className="dim" data-testid="xp-readiness-proceeds">
                  Nothing above stops the run — these are what it will cost.
                  Confirm is available.
                </p>
              ) : null}
            </section>

            {/* ── 5 · CREATIVE AGENT ─────────────────────────────────────── */}
            <section className="card xp-agent" data-testid="xp-agent">
              <div className="card-head">
                <span className="label accent-m">Creative agent</span>
                <span className="xp-agent-live">
                  <i className="dot" aria-hidden="true" />
                  {(doc.conversation || []).length} msg
                  {(doc.conversation || []).length === 1 ? "" : "s"}
                </span>
              </div>
              <p className="xp-agent-what">
                It can see this session's research plan and whatever has come
                back so far. Ask it to steer the research.
              </p>

              <div className="xp-convo" data-testid="xp-convo">
                {(doc.conversation || []).length === 0 ? (
                  <p className="dim">Nothing said yet.</p>
                ) : (doc.conversation || []).slice(-8).map((m, i) => (
                  <p key={i} className={`xp-msg ${m.role === "operator" ? "is-mine" : ""}`}>
                    {m.content}
                  </p>
                ))}
              </div>

              <div className="xp-suggest" data-testid="xp-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s.id} type="button" className="chip"
                          disabled={!canSteer || busy}
                          title={steerWhy || s.text}
                          onClick={() => X.sendMessage(s.text)}
                          data-testid={`xp-suggest-${s.id}`}>
                    {s.text}
                  </button>
                ))}
              </div>
              {steerWhy ? (
                <p className="dim" data-testid="xp-suggest-why">{steerWhy}</p>
              ) : null}

              <div className="xp-ask">
                <label className="sr-only" htmlFor="xp-message">
                  Ask the creative agent about this research
                </label>
                <textarea id="xp-message" rows={2} value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Ask a question or give an instruction…"
                          disabled={!canSteer || busy}
                          data-testid="xp-message-input" />
                <button type="button" className="btn-ghost btn-sm"
                        disabled={!canSteer || busy || !message.trim()}
                        onClick={async () => {
                          const ok = await X.sendMessage(message);
                          if (ok) setMessage("");
                        }}
                        data-testid="xp-message-send">Send</button>
              </div>
            </section>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
